/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useMemo, useCallback } from 'react'
// Third-party
import { css } from '@emotion/react'
import { Switch, Spin, App } from 'antd'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import { paragraphSmall } from 'src/components/css'
import Icon from 'src/components/Icon'
import AddConstraintModal from './AddConstraintModal'
import useConstraintTypes from '../hooks/useConstraintTypes'
import type {
  SchedulerConstraint,
  ReorderItem,
  CreateConstraintData,
} from '../hooks/useSchedulerConstraints'
import type { Venue } from '../hooks/useSchedulerLocations'

/**
 * Division type from parent component
 */
interface Division {
  uid: string
  name: string
}

/**
 * Team type from parent component
 */
interface Team {
  uid: string
  name: string
  divisionUid: string
}

/**
 * Merged card data for display
 */
interface ConstraintCard {
  /** Unique key for React/DnD — constraintId if exists, otherwise typeCode */
  key: string
  typeCode: string
  typeName: string
  typeDescription: string | null
  category: string
  /** Existing constraint from DB, if any */
  constraint: SchedulerConstraint | null
  constraintId: string | null
  isEnabled: boolean
  isHard: boolean
  divisions: string
  teams: string
  sortOrder: number
  /** True when an enabled non-venue constraint has null division_uids or team_uids */
  needsConfiguration: boolean
  /** True for venue-scoped constraints that don't use division/team scope */
  isVenueScope: boolean
  /** Human-readable summary of configured parameters, or null */
  parameterSummary: string | null
}

/**
 * Props for ConstraintsSection
 */
interface ConstraintsSectionProps {
  requestId: string | null
  competitionId?: string
  createRequest?: (scheduleId: string) => Promise<string>
  onRequestCreated?: (requestId: string) => void
  divisions?: Division[]
  teams?: Team[]
  venues?: Venue[]
  constraints: SchedulerConstraint[]
  loading: boolean
  toggleEnabled: (constraintId: string, enabled: boolean) => Promise<void>
  updateConstraint: (
    constraintId: string,
    data: Partial<{
      parameters: Record<string, unknown>
      description: string
      is_hard: boolean
      is_enabled: boolean
      priority: number
      weight: number
      division_uids: string[] | null
      team_uids: string[] | null
      sort_order: number
    }>
  ) => Promise<SchedulerConstraint>
  updateConstraintType: (constraintId: string, isHard: boolean) => Promise<void>
  reorderConstraints: (orderedItems: ReorderItem[]) => Promise<void>
  deleteConstraint: (constraintId: string) => Promise<void>
  addConstraint: (
    data: CreateConstraintData,
    overrideRequestId?: string
  ) => Promise<SchedulerConstraint>
}

/**
 * Default parameters for each constraint type when enabling for the first time
 */
const DEFAULT_PARAMS: Record<string, Record<string, unknown>> = {
  min_rest_days: { min_days: 1 },
  venue_capacity: { facility_uids: [], max_concurrent_games: 1 },
  max_games_per_team_per_day: { max_games: 2 },
  games_per_team_per_week: { games_per_week: 1 },
}

/**
 * Map API category to display category
 */
const mapCategoryToDisplay = (category: string): string => {
  switch (category) {
    case 'venue':
      return 'Availability'
    case 'time':
      return 'Time'
    case 'fairness':
      return 'Game'
    case 'preference':
      return 'Dates'
    case 'distribution':
      return 'Distribution'
    default:
      return category
  }
}

/**
 * Format division UIDs to display string
 */
const formatDivisions = (
  divisionUids: string[] | null,
  divisions: Division[]
): string => {
  if (!divisionUids || divisionUids.length === 0) {
    return 'No divisions selected'
  }

  const divisionNames = divisionUids
    .map((uid) => {
      const division = divisions.find((d) => d.uid === uid)
      return division?.name || uid
    })
    .filter(Boolean)

  if (divisionNames.length === 0) {
    return 'No divisions selected'
  }

  if (divisionNames.length <= 2) {
    return divisionNames.join(', ')
  }

  return `${divisionNames[0]}, ${divisionNames[1]} +${divisionNames.length - 2}`
}

/**
 * Format team UIDs to display string
 */
const formatTeams = (teamUids: string[] | null, teams: Team[]): string => {
  if (!teamUids || teamUids.length === 0) {
    return 'No teams selected'
  }

  const teamNames = teamUids
    .map((uid) => {
      const team = teams.find((t) => t.uid === uid)
      return team?.name || uid
    })
    .filter(Boolean)

  if (teamNames.length === 0) {
    return 'No teams selected'
  }

  if (teamNames.length <= 2) {
    return teamNames.join(', ')
  }

  return `${teamNames[0]}, ${teamNames[1]} +${teamNames.length - 2}`
}

/**
 * Format constraint parameters to a human-readable summary
 */
const formatParameters = (
  typeCode: string,
  parameters: Record<string, unknown>,
  facilities: { uid: string; name: string }[]
): string | null => {
  switch (typeCode) {
    case 'venue_capacity': {
      const facilityUids = (parameters.facility_uids ?? []) as string[]
      const maxConcurrent = (parameters.max_concurrent_games ?? 1) as number
      if (facilityUids.length === 0) {
        return null
      }
      const names = facilityUids
        .map((uid) => facilities.find((f) => f.uid === uid)?.name ?? uid)
        .filter(Boolean)
      const nameStr =
        names.length <= 2
          ? names.join(', ')
          : `${names[0]}, ${names[1]} +${names.length - 2}`
      return `${nameStr} · Max ${maxConcurrent} concurrent`
    }
    case 'min_rest_days': {
      const days = (parameters.min_days ?? 1) as number
      return `${days} day${days !== 1 ? 's' : ''} minimum rest`
    }
    case 'max_games_per_team_per_day': {
      const max = (parameters.max_games ?? 2) as number
      return `Max ${max} game${max !== 1 ? 's' : ''} per day`
    }
    case 'games_per_team_per_week': {
      const perWeek = (parameters.games_per_week ?? 1) as number
      return `${perWeek} game${perWeek !== 1 ? 's' : ''} per week`
    }
    default:
      return null
  }
}

// Constraint type codes managed separately (not shown in this section)
const EXCLUDED_CONSTRAINT_CODES = ['venue_blackout', 'team_bye_period']

// Constraint types that use venue-level scope (not division/team scope)
const VENUE_SCOPE_CONSTRAINT_CODES = ['venue_capacity', 'venue_blackout']

/**
 * Draggable card wrapper for enabled constraint cards
 */
const DraggableCard: React.FC<{
  id: string
  disabled?: boolean
  children: React.ReactNode
}> = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

export const ConstraintsSection: React.FC<ConstraintsSectionProps> = ({
  requestId,
  competitionId,
  createRequest,
  onRequestCreated,
  divisions = [],
  teams = [],
  venues = [],
  constraints,
  loading,
  toggleEnabled,
  updateConstraint,
  updateConstraintType,
  reorderConstraints,
  addConstraint,
}) => {
  const { message } = App.useApp()
  const { constraintTypes, loading: typesLoading } = useConstraintTypes()

  // Extract facilities from venues for the modal
  const venuesForModal = useMemo(
    () => venues.map((v) => ({ uid: v.id, name: v.name })),
    [venues]
  )

  const facilitiesForModal = useMemo(
    () =>
      venues.flatMap((v) =>
        v.facilities.map((f) => ({
          uid: f.id,
          name: `${v.name} - ${f.name}`,
        }))
      ),
    [venues]
  )

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingConstraint, setEditingConstraint] =
    useState<SchedulerConstraint | null>(null)
  const [togglingCodes, setTogglingCodes] = useState<Set<string>>(new Set())

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  /**
   * Ensure a scheduler request exists, creating one if needed.
   * Returns the effective request ID.
   */
  const ensureRequestId = useCallback(async (): Promise<string> => {
    if (requestId) {
      return requestId
    }
    if (!createRequest || !competitionId) {
      throw new Error('Request ID is required')
    }
    const newRequestId = await createRequest(competitionId)
    onRequestCreated?.(newRequestId)
    return newRequestId
  }, [requestId, createRequest, competitionId, onRequestCreated])

  /**
   * Build merged card list: one card per constraint type, merged with existing constraints
   */
  const cardList = useMemo(() => {
    // Filter out excluded constraint types from saved constraints
    const relevantConstraints = constraints.filter(
      (c) => !EXCLUDED_CONSTRAINT_CODES.includes(c.type.code)
    )

    // Build a map of typeCode -> existing constraint
    const constraintByType = new Map<string, SchedulerConstraint>()
    for (const c of relevantConstraints) {
      // If multiple constraints of the same type exist, use the first one
      if (!constraintByType.has(c.type.code)) {
        constraintByType.set(c.type.code, c)
      }
    }

    const enabledCards: ConstraintCard[] = []
    const disabledCards: ConstraintCard[] = []

    for (const cType of constraintTypes) {
      const existing = constraintByType.get(cType.code)
      const displayCategory = mapCategoryToDisplay(cType.category)

      const isVenueScope = VENUE_SCOPE_CONSTRAINT_CODES.includes(cType.code)
      const divisionLabel = existing
        ? formatDivisions(existing.division_uids, divisions)
        : 'No divisions selected'
      const teamLabel = existing
        ? formatTeams(existing.team_uids, teams)
        : 'No teams selected'

      // A non-venue constraint needs configuration when division_uids or team_uids is null
      const isEnabled = existing ? existing.is_enabled : false
      const scopeUnconfigured =
        !isVenueScope &&
        isEnabled &&
        (!existing ||
          existing.division_uids === null ||
          existing.team_uids === null)

      // venue_capacity needs configuration when facility_uids is empty/missing
      const facilityUids = (existing?.parameters as { facility_uids?: string[] })
        ?.facility_uids
      const venueUnconfigured =
        cType.code === 'venue_capacity' &&
        isEnabled &&
        (!facilityUids || facilityUids.length === 0)

      const parameterSummary =
        isEnabled && existing
          ? formatParameters(
              cType.code,
              (existing.parameters as Record<string, unknown>) || {},
              facilitiesForModal
            )
          : null

      const card: ConstraintCard = {
        key: existing ? existing.id : `virtual-${cType.code}`,
        typeCode: cType.code,
        typeName: cType.name,
        typeDescription: cType.description,
        category: displayCategory,
        constraint: existing || null,
        constraintId: existing?.id || null,
        isEnabled,
        isHard: existing ? existing.is_hard : false,
        divisions: divisionLabel,
        teams: teamLabel,
        sortOrder: existing ? existing.sort_order : cType.default_priority,
        needsConfiguration: scopeUnconfigured || venueUnconfigured,
        isVenueScope,
        parameterSummary,
      }

      if (card.isEnabled) {
        enabledCards.push(card)
      } else {
        disabledCards.push(card)
      }
    }

    // Enabled cards sorted by sort_order
    enabledCards.sort((a, b) => a.sortOrder - b.sortOrder)
    // Disabled cards sorted by default priority
    disabledCards.sort((a, b) => a.sortOrder - b.sortOrder)

    return [...enabledCards, ...disabledCards]
  }, [constraintTypes, constraints, divisions, teams, facilitiesForModal])

  const enabledCardKeys = useMemo(
    () => cardList.filter((c) => c.isEnabled).map((c) => c.key),
    [cardList]
  )

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)

      if (!over || active.id === over.id) {
        return
      }

      // Only reorder among enabled cards
      const oldIndex = enabledCardKeys.indexOf(String(active.id))
      const newIndex = enabledCardKeys.indexOf(String(over.id))

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const enabledCards = cardList.filter((c) => c.isEnabled)
        const reordered = [...enabledCards]
        const [removed] = reordered.splice(oldIndex, 1)
        reordered.splice(newIndex, 0, removed)

        const reorderPayload: ReorderItem[] = reordered
          .filter((c) => c.constraintId)
          .map((item, index) => ({
            id: item.constraintId!,
            sort_order: index,
          }))

        try {
          await reorderConstraints(reorderPayload)
        } catch {
          message.error('Failed to reorder constraints')
        }
      }
    },
    [cardList, enabledCardKeys, reorderConstraints, message]
  )

  /**
   * Handle toggle ON/OFF for a constraint card
   */
  const handleToggle = useCallback(
    async (card: ConstraintCard, checked: boolean) => {
      setTogglingCodes((prev) => new Set(prev).add(card.typeCode))
      try {
        if (checked) {
          if (card.constraintId) {
            // Existing constraint — just re-enable
            await toggleEnabled(card.constraintId, true)
          } else {
            // No existing constraint — create with defaults
            const effectiveRequestId = await ensureRequestId()
            await addConstraint(
              {
                constraint_type_code: card.typeCode,
                parameters: DEFAULT_PARAMS[card.typeCode] || {},
                is_hard: card.isHard,
                is_enabled: true,
              },
              effectiveRequestId
            )
          }
        } else {
          if (card.constraintId) {
            await toggleEnabled(card.constraintId, false)
          }
        }
      } catch {
        message.error('Failed to toggle constraint')
      } finally {
        setTogglingCodes((prev) => {
          const next = new Set(prev)
          next.delete(card.typeCode)
          return next
        })
      }
    },
    [toggleEnabled, addConstraint, ensureRequestId, message]
  )

  /**
   * Handle SOFT/HARD toggle
   */
  const handleTypeChange = useCallback(
    async (card: ConstraintCard, newType: 'SOFT' | 'HARD') => {
      const isHard = newType === 'HARD'
      if (card.isHard === isHard) {
        return
      }
      if (!card.constraintId) {
        return
      }

      try {
        await updateConstraintType(card.constraintId, isHard)
      } catch {
        message.error('Failed to update constraint type')
      }
    },
    [updateConstraintType, message]
  )

  const handleAddModalClose = useCallback(() => {
    setIsAddModalOpen(false)
    setEditingConstraint(null)
  }, [])

  const handleAddConstraintSubmit = useCallback(
    async (data: CreateConstraintData) => {
      const effectiveRequestId = await ensureRequestId()
      await addConstraint(data, effectiveRequestId)
    },
    [addConstraint, ensureRequestId]
  )

  const handleEditConstraint = useCallback(
    (constraintId: string) => {
      const constraint = constraints.find((c) => c.id === constraintId)
      if (constraint) {
        setEditingConstraint(constraint)
        setIsAddModalOpen(true)
      }
    },
    [constraints]
  )

  const handleUpdateConstraint = useCallback(
    async (
      constraintId: string,
      data: Partial<{
        parameters: Record<string, unknown>
        description: string
        is_hard: boolean
        is_enabled: boolean
        division_uids: string[] | null
        team_uids: string[] | null
      }>
    ) => {
      await updateConstraint(constraintId, data)
      setIsAddModalOpen(false)
      setEditingConstraint(null)
    },
    [updateConstraint]
  )

  // --- Render helpers ---

  const getCategoryStyles = (category: string) => {
    switch (category) {
      case 'Time':
        return {
          borderColor: colors.WARNING_200,
          color: colors.WARNING_200,
          backgroundColor: 'rgba(58, 41, 5, 0.3)',
        }
      case 'Game':
        return {
          borderColor: colors.SUCCESS_200,
          color: colors.SUCCESS_200,
          backgroundColor: 'rgba(5, 58, 17, 0.3)',
        }
      case 'Availability':
        return {
          borderColor: '#FF8800',
          color: '#FF8800',
          backgroundColor: 'rgba(58, 30, 5, 0.3)',
        }
      case 'Dates':
        return {
          borderColor: colors.ERROR_100,
          color: colors.ERROR_100,
          backgroundColor: 'rgba(132, 16, 16, 0.3)',
        }
      case 'Distribution':
        return {
          borderColor: colors.SUCCESS_200,
          color: colors.SUCCESS_200,
          backgroundColor: 'rgba(5, 58, 17, 0.3)',
        }
      default:
        return {
          borderColor: colors.NEUTRAL_100,
          color: colors.NEUTRAL_100,
          backgroundColor: 'transparent',
        }
    }
  }

  const renderCategoryBadge = (category: string) => {
    const styles = getCategoryStyles(category)
    return (
      <span
        css={css`
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 17.4px;
          padding: 2px 4px;
          background-color: ${styles.backgroundColor};
          border: 1px solid ${styles.borderColor};
          color: ${styles.color};
          border-radius: 4px;
          white-space: nowrap;
        `}
      >
        {category}
      </span>
    )
  }

  const renderTypeBadge = (
    type: 'SOFT' | 'HARD',
    onTypeChange?: (newType: 'SOFT' | 'HARD') => void
  ) => (
    <div
      css={css`
        display: flex;
        border-radius: 4px;
        overflow: hidden;
      `}
    >
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation()
          onTypeChange?.('SOFT')
        }}
        css={css`
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
          letter-spacing: 1.6px;
          padding: 4px 8px;
          background-color: ${type === 'SOFT'
            ? colors.SUCCESS_500
            : colors.SECONDARY_400};
          color: ${colors.NEUTRAL_30};
          text-transform: uppercase;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease;

          &:hover {
            background-color: ${type === 'SOFT'
              ? colors.SUCCESS_500
              : colors.SUCCESS_400};
          }
        `}
      >
        SOFT
      </button>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation()
          onTypeChange?.('HARD')
        }}
        css={css`
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
          letter-spacing: 1.6px;
          padding: 4px 8px;
          background-color: ${type === 'HARD'
            ? colors.ERROR_500
            : colors.SECONDARY_400};
          color: ${colors.NEUTRAL_30};
          text-transform: uppercase;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease;

          &:hover {
            background-color: ${type === 'HARD'
              ? colors.ERROR_500
              : colors.ERROR_400};
          }
        `}
      >
        HARD
      </button>
    </div>
  )

  /**
   * Render a single constraint card
   */
  const renderCard = (card: ConstraintCard) => {
    const isToggling = togglingCodes.has(card.typeCode)

    return (
      <div
        key={card.key}
        css={css`
          background-color: ${colors.SECONDARY_400};
          border: 1px solid ${colors.SECONDARY_200};
          border-radius: 8px;
          padding: 16px;
          transition: opacity 0.2s ease;
          ${!card.isEnabled ? 'opacity: 0.55;' : ''}
        `}
      >
        {/* Row 1: Grip, Name, Category, SOFT/HARD, ON/OFF */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
          `}
        >
          {/* Drag handle — only for enabled cards */}
          <div
            css={css`
              width: 16px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
              visibility: ${card.isEnabled ? 'visible' : 'hidden'};
              cursor: ${card.isEnabled ? 'grab' : 'default'};
              &:active {
                cursor: grabbing;
              }
            `}
          >
            <Icon name='grip-lines' fontSize={14} color={colors.NEUTRAL_100} />
          </div>

          {/* Name */}
          <span
            css={css`
              ${paragraphSmall}
              font-weight: 600;
              color: ${colors.WHITE};
              white-space: nowrap;
            `}
          >
            {card.typeName}
          </span>

          {/* Category badge */}
          {renderCategoryBadge(card.category)}

          {/* Spacer */}
          <div
            css={css`
              flex: 1;
            `}
          />

          {/* SOFT/HARD toggle — only for enabled cards */}
          {card.isEnabled &&
            renderTypeBadge(card.isHard ? 'HARD' : 'SOFT', (newType) =>
              handleTypeChange(card, newType)
            )}

          {/* ON/OFF switch */}
          <Switch
            checked={card.isEnabled}
            loading={isToggling}
            onChange={(checked) => handleToggle(card, checked)}
            size='small'
            aria-label={`Toggle constraint: ${card.typeName}`}
            css={css`
              &.ant-switch-checked {
                background-color: ${colors.PRIMARY_300} !important;
              }
            `}
            onClick={(_, e) => e.stopPropagation()}
          />
        </div>

        {/* Row 2: Description */}
        {card.typeDescription && (
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 400;
              line-height: 17.4px;
              color: ${colors.NEUTRAL_50};
              margin: 8px 0 0 28px;
            `}
          >
            {card.typeDescription}
          </p>
        )}

        {/* Row 3: Parameter summary — only for enabled cards with parameters */}
        {card.isEnabled && card.parameterSummary && !card.isVenueScope && (
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 500;
              line-height: 17.4px;
              color: ${colors.NEUTRAL_50};
              margin: 8px 0 0 28px;
            `}
          >
            {card.parameterSummary}
          </p>
        )}

        {/* Row 4: Scope + Configure + Delete — only for enabled cards */}
        {card.isEnabled && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
              margin-top: 8px;
              padding-left: 28px;
            `}
          >
            {card.isVenueScope ? (
              card.parameterSummary ? (
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 17.4px;
                    color: ${colors.NEUTRAL_50};
                  `}
                >
                  {card.parameterSummary}
                </span>
              ) : card.needsConfiguration ? (
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 17.4px;
                    color: ${colors.ERROR_200};
                  `}
                >
                  No facilities selected
                </span>
              ) : null
            ) : (
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 17.4px;
                  color: ${card.needsConfiguration
                    ? colors.ERROR_200
                    : colors.NEUTRAL_100};
                `}
              >
                {card.divisions} &middot; {card.teams}
              </span>
            )}

            {/* Spacer */}
            <div
              css={css`
                flex: 1;
              `}
            />

            {/* Configure button */}
            {card.constraintId && (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation()
                  handleEditConstraint(card.constraintId!)
                }}
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 12px;
                  font-weight: 500;
                  color: ${colors.PRIMARY_300};
                  background: transparent;
                  border: 1px solid ${colors.PRIMARY_300};
                  border-radius: 6px;
                  padding: 4px 12px;
                  cursor: pointer;
                  transition: background 0.2s ease;
                  white-space: nowrap;

                  &:hover {
                    background: ${colors.PRIMARY_300}20;
                  }
                `}
              >
                Configure
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  // Get active card for drag overlay
  const activeCard = activeId
    ? cardList.find((c) => c.key === String(activeId))
    : null

  const isLoading = loading && cardList.length === 0 && typesLoading

  return (
    <>
      {/* Header */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 16px;
        `}
      >
        <div>
          <h2
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              line-height: 16.8px;
              letter-spacing: 1.68px;
              text-transform: uppercase;
              color: ${colors.NEUTRAL_0};
              margin: 0 0 4px 0;
            `}
          >
            Constraints
          </h2>
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 400;
              line-height: 17.4px;
              color: ${colors.NEUTRAL_50};
              margin: 0;
            `}
          >
            Hard constraints must be met to generate a schedule.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 48px;
          `}
        >
          <Spin size='large' />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={enabledCardKeys}
            strategy={verticalListSortingStrategy}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              {cardList.map((card) =>
                card.isEnabled ? (
                  <DraggableCard key={card.key} id={card.key}>
                    {renderCard(card)}
                  </DraggableCard>
                ) : (
                  <div key={card.key}>{renderCard(card)}</div>
                )
              )}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeCard ? (
              <div
                css={css`
                  background-color: ${colors.SECONDARY_300};
                  padding: 12px 16px;
                  border-radius: 8px;
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                  color: ${colors.WHITE};
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  display: flex;
                  align-items: center;
                  gap: 12px;
                `}
              >
                <Icon
                  name='grip-lines'
                  fontSize={14}
                  color={colors.NEUTRAL_100}
                />
                <span>{activeCard.typeName}</span>
                {renderCategoryBadge(activeCard.category)}
                <span
                  css={css`
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: 600;
                    background-color: ${activeCard.isHard
                      ? colors.ERROR_500
                      : colors.SUCCESS_500};
                  `}
                >
                  {activeCard.isHard ? 'HARD' : 'SOFT'}
                </span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <AddConstraintModal
        open={isAddModalOpen}
        onClose={handleAddModalClose}
        onSubmit={handleAddConstraintSubmit}
        onUpdate={handleUpdateConstraint}
        divisions={divisions}
        teams={teams}
        venues={venuesForModal}
        facilities={facilitiesForModal}
        editingConstraint={editingConstraint}
      />
    </>
  )
}

export default ConstraintsSection
